import Icon, { IconName } from '@features/ui-atoms/components/Icon';
import theme from '@features/ui-layout/styles/theme';
import { styled } from '@mui/joy';

/** Chip variants */
export enum ChipVariant {
  BLUE = 'BLUE',
  BLUE_LIGHT = 'BLUE_LIGHT',
  DEFAULT = 'DEFAULT',
  TEXT = 'TEXT',
  ICON = 'ICON',
  ICON_LIGHT = 'ICON_LIGHT',
  ORANGE = 'ORANGE',
}

interface ChipProps {
  endIcon?: IconName;
  label?: string;
  iconSize?: number;
  startIcon?: IconName;
  variant?: ChipVariant;
}

/**
 * Chip component
 * @param param0
 * @param param0.endIcon icon at the end of the chip
 * @param param0.label text of the chip
 * @param param0.startIcon icon at the start of the chip
 * @param param0.variant variant of the chip
 */
const Chip = ({ endIcon, label, startIcon, iconSize, variant = ChipVariant.DEFAULT }: ChipProps) => (
  <ChipWrapper variant={variant}>
    {startIcon ? <Icon size={iconSize || 16} name={startIcon} /> : null}
    {label}
    {endIcon ? <Icon size={iconSize || 16} name={endIcon} /> : null}
  </ChipWrapper>
);

type StyleDef = {
  [variant in ChipVariant]: {
    backgroundColor: string;
    textColor: string;
  };
};

const styles: StyleDef = {
  [ChipVariant.DEFAULT]: {
    backgroundColor: theme.palette.grey2,
    textColor: theme.palette.grey4,
  },
  [ChipVariant.BLUE]: {
    backgroundColor: theme.palette.blueLight,
    textColor: theme.palette.blueDark,
  },
  [ChipVariant.BLUE_LIGHT]: {
    backgroundColor: theme.palette.blueLight,
    textColor: theme.palette.bluePrimary,
  },
  [ChipVariant.TEXT]: {
    backgroundColor: 'none',
    textColor: theme.palette.grey4,
  },
  [ChipVariant.ICON]: {
    backgroundColor: theme.palette.grey1,
    textColor: theme.palette.grey5,
  },
  [ChipVariant.ICON_LIGHT]: {
    backgroundColor: theme.palette.grey2,
    textColor: theme.palette.grey5,
  },
  [ChipVariant.ORANGE]: {
    backgroundColor: theme.palette.orange3,
    textColor: theme.palette.orange1,
  },
};

export default Chip;

const ChipWrapper = styled('span')<{ variant: ChipVariant }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  padding: 3px 8px;
  background-color: ${({ variant }) => styles[variant].backgroundColor};
  border-radius: 8px;
  ${theme.typography['legend-regular']}
  color: ${({ variant }) => styles[variant].textColor};
  height: fit-content;
`;
